import type { ProductDocumentKind } from "@private-fiction/story-core";
import { runtimeConfig } from "../config/runtimeConfig";
import { inheritedSyncFailure, recordSyncFailure, type SyncFailureState } from "./syncFailure";

export interface LocalAccountDocument {
  key: string;
  scope: string;
  kind: ProductDocumentKind;
  localId: string;
  revision: number;
  payload: Record<string, unknown>;
  deletedAt: string | null;
  changeSeq: number;
  updatedAt: string;
}

export interface PendingAccountMutation extends SyncFailureState {
  id: string;
  key: string;
  scope: string;
  kind: ProductDocumentKind;
  localId: string;
  operation: "upsert" | "delete";
  expectedRevision: number;
  payload: Record<string, unknown>;
  createdAt: string;
}

interface MetadataRecord { key: string; value: number }

// A new Cloud project starts with a separate library and outbox. Never adopt
// pending writes from a previous project merely because the app URL is unchanged.
const databaseName = `mvp-story-account-v1:${runtimeConfig.supabaseUrl || "offline"}`;
const databaseVersion = 1;
const documentKey = (scope: string, kind: ProductDocumentKind, localId: string) => `${scope}\u0000${kind}\u0000${localId}`;
const metadataKey = (scope: string, name: string) => `${scope}\u0000${name}`;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

async function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("documents")) {
        const store = database.createObjectStore("documents", { keyPath: "key" });
        store.createIndex("scope", "scope", { unique: false });
      }
      if (!database.objectStoreNames.contains("outbox")) {
        const store = database.createObjectStore("outbox", { keyPath: "id" });
        store.createIndex("scope", "scope", { unique: false });
        store.createIndex("key", "key", { unique: false });
      }
      if (!database.objectStoreNames.contains("metadata")) database.createObjectStore("metadata", { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Cannot open account cache"));
  });
}

export async function loadAccountDocuments(scope: string): Promise<LocalAccountDocument[]> {
  const database = await openDatabase();
  const transaction = database.transaction("documents", "readonly");
  const records = await requestResult(transaction.objectStore("documents").index("scope").getAll(scope)) as LocalAccountDocument[];
  await transactionDone(transaction);
  database.close();
  return records.filter((record) => !record.deletedAt);
}

export async function loadAccountDocument(scope: string, kind: ProductDocumentKind, localId: string): Promise<LocalAccountDocument | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction("documents", "readonly");
  const record = await requestResult(transaction.objectStore("documents").get(documentKey(scope, kind, localId))) as LocalAccountDocument | undefined;
  await transactionDone(transaction);
  database.close();
  return record;
}

export async function saveLocalAccountDocument(scope: string, kind: ProductDocumentKind, localId: string, payload: Record<string, unknown>): Promise<LocalAccountDocument> {
  const database = await openDatabase();
  const transaction = database.transaction(["documents", "outbox"], "readwrite");
  const documents = transaction.objectStore("documents");
  const outbox = transaction.objectStore("outbox");
  const key = documentKey(scope, kind, localId);
  const current = await requestResult(documents.get(key)) as LocalAccountDocument | undefined;
  const existingMutations = await requestResult(outbox.index("key").getAll(key)) as PendingAccountMutation[];
  existingMutations.forEach((mutation) => outbox.delete(mutation.id));
  const now = new Date().toISOString();
  const record: LocalAccountDocument = {
    key, scope, kind, localId, revision: current?.revision ?? 0, payload,
    deletedAt: null, changeSeq: current?.changeSeq ?? 0, updatedAt: now,
  };
  const mutation: PendingAccountMutation = {
    id: crypto.randomUUID(), key, scope, kind, localId, operation: "upsert",
    expectedRevision: record.revision, payload, createdAt: now,
    ...inheritedSyncFailure(existingMutations),
  };
  documents.put(record);
  outbox.put(mutation);
  await transactionDone(transaction);
  database.close();
  return record;
}

export async function listPendingAccountMutations(scope: string): Promise<PendingAccountMutation[]> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readonly");
  const records = await requestResult(transaction.objectStore("outbox").index("scope").getAll(scope)) as PendingAccountMutation[];
  await transactionDone(transaction);
  database.close();
  return records.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export async function noteAccountMutationFailure(mutation: PendingAccountMutation, error: unknown): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readwrite");
  const store = transaction.objectStore("outbox");
  const current = await requestResult(store.index("key").getAll(mutation.key)) as PendingAccountMutation[];
  // An edit may replace the submitted entry while transport is in flight. Keep
  // the newer payload, but quarantine descendants of the same rejected revision.
  for (const item of current) if (item.expectedRevision === mutation.expectedRevision) {
    store.put({ ...item, ...recordSyncFailure(item, error) });
  }
  await transactionDone(transaction);
  database.close();
}

export async function acceptAccountMutation(scope: string, mutation: PendingAccountMutation, revision: number, changeSeq: number): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(["documents", "outbox"], "readwrite");
  const documents = transaction.objectStore("documents");
  const current = await requestResult(documents.get(mutation.key)) as LocalAccountDocument | undefined;
  if (current) documents.put({ ...current, revision, changeSeq, updatedAt: new Date().toISOString() });
  const outbox = transaction.objectStore("outbox");
  outbox.delete(mutation.id);
  // A newer local edit may have replaced the submitted outbox entry while the
  // request was in flight. Advance only descendants of this accepted local
  // write; they otherwise conflict with our own acknowledged revision.
  const descendants = await requestResult(outbox.index("key").getAll(mutation.key)) as PendingAccountMutation[];
  for (const next of descendants) if (next.expectedRevision === mutation.expectedRevision && next.createdAt >= mutation.createdAt) outbox.put({ ...next, expectedRevision: revision });
  await transactionDone(transaction);
  database.close();
}

export async function rebaseAccountMutation(mutation: PendingAccountMutation, revision: number): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readwrite");
  const outbox = transaction.objectStore("outbox");
  const current = await requestResult(outbox.get(mutation.id)) as PendingAccountMutation | undefined;
  if (current) {
    outbox.delete(mutation.id);
    outbox.put({ ...current, id: crypto.randomUUID(), expectedRevision: revision, createdAt: new Date().toISOString() });
  }
  await transactionDone(transaction);
  database.close();
}

export async function mergeRemoteAccountDocument(scope: string, remote: Omit<LocalAccountDocument, "key" | "scope">): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(["documents", "outbox"], "readwrite");
  const key = documentKey(scope, remote.kind, remote.localId);
  const pending = await requestResult(transaction.objectStore("outbox").index("key").getAll(key)) as PendingAccountMutation[];
  const documents = transaction.objectStore("documents");
  // Pulling is not permission to rebase unsent local edits onto remote changes.
  if (!pending.length) {
    documents.put({ ...remote, key, scope });
  }
  await transactionDone(transaction);
  database.close();
}

export async function getAccountCursor(scope: string): Promise<number> {
  const database = await openDatabase();
  const transaction = database.transaction("metadata", "readonly");
  const record = await requestResult(transaction.objectStore("metadata").get(metadataKey(scope, "cursor"))) as MetadataRecord | undefined;
  await transactionDone(transaction);
  database.close();
  return Number(record?.value ?? 0);
}

export async function setAccountCursor(scope: string, cursor: number): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("metadata", "readwrite");
  transaction.objectStore("metadata").put({ key: metadataKey(scope, "cursor"), value: cursor } satisfies MetadataRecord);
  await transactionDone(transaction);
  database.close();
}
